import { MenuItemProps } from "@mui/material";
import { Editor } from "tinymce";

export enum Severity {
  Error = "error",
  Info = "info",
  Warning = "warning",
  Success = "success",
}

export interface ISnackbarMessage {
  message: string | undefined;
  severity?: Severity;
  duration?: number;
}

export class SnackbarMessageProps implements ISnackbarMessage {
  constructor(
    message: string | undefined,
    severity = Severity.Success,
    duration = 3000,
  ) {
    this.message = message;
    this.severity = severity;
    this.duration = duration;
  }

  message: string | undefined;
  severity?: Severity;
  duration?: number;
}

export type IdValue = {
  id?: string;
  value: string;
};

export type IdName = {
  id?: string;
  name: string;
};

export enum Mode {
  Update = "update",
  Create = "create",
  View = "view",
}

export type CommonFormProps<TEntity, TRequest> = {
  entity?: TEntity | null;
  submitLabel?: string;
  onCancel?: () => void;
  onSubmit: (values: TRequest) => Promise<any>;
};

export type ContextMenuItem = {
  key: string;
  content: React.ReactNode;
  props?: MenuItemProps & {
    button: true | undefined;
  };
  onClick?: (e: any, id: string) => void;
};

export type PaginatedResult<T> = {
  totalRecords?: number;
  data?: T[];
};

export enum DetailElementType {
  Row = "row",
  Table = "table",
  Box = "box",
}

export enum DeliveryMethodSettingsDataType {
  Url = "URL",
  Boolean = "Boolean",
  String = "String",
  Integer = "Integer",
  Iframe = "Iframe",
}

export enum DeliveryMethodSettingCodeConstants {
  Id = "Id",
  Url = "URL",
  FileName = "FileName",
  FileSize = "FileSize",
  ContentType = "ContentType",
  Iframe = "Iframe",
  PageContent = "PageContent",
  ShowKey = "ShowKey",
  Width = "Width",
  Height = "Height",
}

export enum DeliverableTypeIds {
  Course = "2e5030f1-71c9-4db3-ae0f-fd6e8cdf02c8",
  Handout = "dbbbaee9-8aaa-4b09-8421-19f78238f273",
}

export enum DeliveryMethodId {
  Video = "e5b467c4-3385-4419-befe-1645086af7a5",
  ArticulateRise = "82648929-caa3-46be-9843-8480ad5441c0",
  Pdf = "e870ccf4-3041-49fa-b68f-afb8c7247f8c",
  Vimeo = "8f5a2b1c-3d4e-4f6a-7b8c-9d0e1f2a3b4c",
  ThreePlayMedia = "4bd04d20-bffa-477a-9a72-812637430d54",
  InfographEd = "b47c8b2a-9f1e-4d3a-8c5e-7f2b1a6d9e3c",
  ISpring = "a6c720a3-e27f-44e5-93cb-fbf87bb5b36c",
  Page = "f8a3d7c4-9b2e-4f6a-8c1d-5e7b9f3a2d4c",
  Blubrry = "35d5bfdc-070e-4130-b9d0-d8325c1ebbec",
  Zoom = "5c7b6d38-d1dd-4564-a0d3-fa0cb6630ce6",
  ArcheMedX = "ec62a3ac-ba03-44e8-b86c-58b876bd6607",
  Inxpo = "6ad85c98-8060-4c68-9a73-640709e76777",
  Copient = "114e0415-aed1-4240-8490-cd7e997ecad0",
  URL = "4ff749be-c1d4-4e09-8efb-d557a2240988",
  AnatomyTv = "784d41c0-26c1-4c44-8691-9ead953ce2a0",
}

export enum SkuAvailabilities {
  OnDemand = "OnDemand",
  Live = "Live",
}

export enum UserAgreementStatusId {
  Rejected = "ed5ebb36-e881-4eab-92f4-031aaf320fb0",
  Accepted = "5af03874-8f4c-4ddb-8925-09f9ed815aeb",
  Cancelled = "d4ecb3f4-e244-4d45-9843-2ffaf7d0b63c",
  Pending = "44111c8b-3998-4c5a-8b64-649f9bdd1296",
}

export enum RuleType {
  Product = "Product",
  Sku = "Sku",
  Profession = "Profession",
  Series = "Series",
  Organization = "Organization",
  SkuContent = "SkuContent",
  Tag = "Tag",
}

export type Context = {
  organizationId: string | undefined;
  applicationId: string | undefined;
};

export enum Orientation {
  Landscape = "Landscape",
  Portrait = "Portrait",
}

export enum QuestionTypeEnum {
  TextQuestion = "text-question",
  CategoryHeader = "category-header",
  Rating = "rating-question",
  MultipleChoice = "multiple-choice-question",
  MultipleAnswers = "multiple-answers-question",
  FileUploadVideo = "file-upload-video",
  FileUploadImage = "file-upload-image",
  FileUploadDocument = "file-upload-document",
}

export enum QuestionTypeIdEnum {
  CategoryHeaderId = "b6144c6c-45dd-46c7-8e1e-58ab8acf1e6b",
  MultipleChoiceId = "031abea9-60dd-42b2-a588-f9d00e67fbb6",
  MultipleAnswersId = "3f30c861-e648-4ab6-938a-6851ff9e1bc4",
  RatingId = "4227e559-3b14-4e07-b90e-73151f723698",
  TextQuestionId = "fe47c977-e1da-44e3-8cbe-4e784bae5a33",
  FileUploadVideoId = "f2046695-3936-4b86-bfde-049d168eda41",
  FileUploadImageId = "8c9c5ba8-c41c-4043-85ae-f87d0d222d49",
  FileUploadDocumentId = "1a321202-806e-40d5-a815-3e10aa4adcf8",
}

export enum JobDataStatus {
  Error = "Error",
  Retrying = "Retrying",
  Processed = "Processed",
  Queued = "Queued",
  Unprocessed = "Unprocessed",
  Processing = "Processing",
  Cancelled = "Cancelled",
}

export enum JobStatus {
  Cancelled = "Cancelled",
  Processed = "Processed",
  Unprocessed = "Unprocessed",
  Processing = "Processing",
  ProcessedWithErrors = "Processed With Errors",
  Error = "Error",
}

export enum ImportSourceTypes {
  CSV = "CSV",
  JSON = "JSON",
}

export enum SkuAvailabilityId {
  OnDemand = "7d9ed55d-3919-48ec-9876-2a66e8b85f91",
  Live = "ae00a8f7-bd4c-46d8-a661-b3f6deeea89a",
}

export const SkuAvailabilityName: Record<SkuAvailabilityId, string> = {
  [SkuAvailabilityId.OnDemand]: "OnDemand",
  [SkuAvailabilityId.Live]: "Live",
};

export enum ProductTypeIds {
  Package = "EED38F41-C74B-4F5D-8E79-7EEAAD703F8A",
  Membership = "7F909871-6DDD-4551-B78A-F990C2F2AAE2",
  Catalog = "81290F8E-A3C7-4D98-B1C3-F7947C928789",
}

export enum FilterPages {
  SkuQualificationList = "SkuQualificationList",
}

export enum SettingType {
  Group = "group",
}

export enum UserSkuStates {
  Expired = "Expired",
  Deactivated = "Deactivated",
  Completed = "Completed",
  InProgress = "InProgress",
  Active = "Active",
}

export enum ActivityTypes {
  ExamAttempt = "ExamAttempt",
}

export enum ActivityEvents {
  GradingPending = "GradingPending",
}

export type TinyMCEPluginBase = {
  editor: Editor;
  onClose: () => void;
};

export enum EvaluationTypeIdConstants {
  Exam = "e1b18288-52be-4cbd-bb6d-08094df32880",
  Survey = "bf55b026-6b9b-46a7-a3df-47751bf47e20",
  Evaluation = "05a699ba-348c-4598-93fc-9692c5a34fc3",
}

export enum EvaluationFormMode {
  Create = "Create",
  Update = "Update",
}

export enum SettingTypeId {
  MediaLibraryAsset = "dc7da36c-4d6e-49a5-80f8-cf53a4f84aad",
  Color = "14626986-b14c-47f6-a803-28a1e9517750",
  Html = "1bae59c9-5b79-4eab-b376-a5dbeb524d43",
  Bool = "f66c2a8d-7efa-46e1-8e87-53bf9b867b96",
  Json = "156f0e84-4b58-437f-b35b-3de82407ad9d",
  Int = "ce7fd610-a3ed-447c-a092-8ee2180a98b2",
  Decimal = "ed500eb5-f1b6-475b-b3ce-f974fb22cf1b",
  Date = "bcb88df0-cb67-4c3a-b44a-5e4c2eebcc4d",
  DateTime = "f632b913-7140-4cfd-b756-b7d4d1a40e8a",
  Time = "914b25b0-7d2e-4b98-9f64-0aaca557c692",
}

export enum SettingGroupId {
  Features = "6327ff49-a46f-453d-800b-eccf8be32209",
}

export enum UserStatusType {
  Active = "active",
  Pending = "pending",
  Inactive = "inactive",
  Unset = "unset",
}

export enum VerificationStatusIds {
  Success = "9300c3ba-d62c-4e1e-bff0-3ad1a4a55255",
  Failure = "b47230b2-dd8f-45db-bded-2de72be3e234",
  Pending = "00d99a8f-bc5e-4beb-9947-ee541b51f65d",
  NoValidationAvailable = "ce2a54d4-3564-466b-b7a7-f15d54e12c0a",
}

export enum OrganizationType {
  Brand = "47db19f9-5a5c-42eb-8782-ac86420f8f9b",
  Configuration = "1a73c979-678b-4a69-adb7-cae8851b6dad",
  Client = "b5702924-8952-483b-a15e-bab06fc7f5bd",
}

export enum OrganizationStatus {
  Active = "4e14d870-7ac4-47b2-83e9-9abca62600f1",
  Deactivated = "a949c67e-f234-48d0-a238-7f2f089aee94",
}

export enum UserEvaluationStatusConstants {
  Completed = "Completed",
}

export enum Environments {
  Local = "local",
  Dev = "dev",
  Qa = "qa",
  Prod = "prod",
}

export enum ProfessionIds {
  AnyProfession = "EC40B67D-6C5F-4FA6-B418-32794084A6CB",
}

export enum TemplateEntityIds {
  SkuAgreement = "9114B9BF-23A6-4269-AC2F-EBF1664245C7",
}

export enum TemplateTypeIds {
  SkuAgreements = "0892F218-095E-4E8D-918F-B9D8D413B3F9",
}

export enum AgreementStatusIds {
  Active = "953B994A-D34B-41E1-8911-1F25C63B7945",
  Deactivated = "373F8212-A36A-4C81-A6FC-87A047CADAAE",
  Draft = "A492205D-4F06-44D8-8056-C52D51DBE31C",
}

export enum AgreementStatuses {
  Active = "Active",
  Deactivated = "Deactivated",
  Draft = "Draft",
}

export enum SkuUserAgreementStatusIds {
  Accepted = "5af03874-8f4c-4ddb-8925-09f9ed815aeb",
  Pending = "44111c8b-3998-4c5a-8b64-649f9bdd1296",
}

export enum AttributeDisplayTypes {
  AttributeSection = "c4e3fa58-8e19-40ec-a30b-0a36c4d6a91b",
  Accordion = "59c3219f-b9ef-45c8-a404-e32d4eaebc25",
}

export enum AttributeCodeConstants {
  Locations = "locations",
  Sponsors = "sponsors",
  CoverImage = "coverimage",
}

export enum SponsorMetaValueNames {
  SponsorTypeId = "SponsorTypeId",
  SponsorType = "SponsorType",
}

export enum SponsorTypeNames {
  Sponsor = "sponsor",
  JointProvider = "jointprovider",
}

export enum SponsorTypeIds {
  Sponsor = "2D81D180-C7E2-44CF-8F46-79A26FB5E0CC",
  JointProvider = "ED015F02-7CF8-41DF-8551-8280B7115325",
}

export enum LocationMetaValueNames {
  Company = "Company",
  Address1 = "Address1",
  City = "City",
  State = "State",
  LocationType = "LocationType",
}

export enum ProgressionGateTypes {
  None = "B3439450-425E-4D7E-A9A2-AAA3CC6B2542",
}

export enum ProgressionGateStatus {
  None = "none",
  Deactivated = "deactivated",
  Inherited = "inherited",
  Overridden = "overridden",
}

export enum MenuItemTypeIds {
  Url = "cc7aef1e-ed83-4ce8-ad7e-07cf614ad3d2",
  SkuContent = "fb480abb-3b30-44bf-8d46-08d66c0ac371",
}

export enum SettingsCodeConstants {
  RemoveEmailFromManualCreditSubmission = "RemoveEmailFromManualCreditSubmission",
}

export enum ProcessTypeIds {
  BuildCatalog = "162979e1-40c6-4911-b601-0ec9eedd103b",
  ScormCourseExport = "ec85d176-cb91-4b1e-9d2d-797a6bbf3aae",
  SyncCatalogFromCRM = "f89a8144-019a-49ac-b30f-1c7444c06c5b",
  UpdateEvaluations = "b292fdbf-dd29-476c-aec9-290bd1923ace",
}

export enum SkuStatusIds {
  Draft = "a6c7a63a-158d-4241-8d3e-314cca1a5c9c",
  Published = "1b895592-fb30-41a9-9330-c0055ecf7ac9",
  Archived = "911c9765-b733-4712-a7b3-37a4b6948e3e",
}

export enum AttributeTypes {
  Topics = "Topics",
}

export enum AttributeMetaValues {
  Color = "Color",
  MediaLibraryItem = "MediaLibraryItem",
  Description = "Description",
  LocationTypeId = "LocationTypeId",
  Company = "Company",
  Address1 = "Address1",
  Address2 = "Address2",
  Address3 = "Address3",
  City = "City",
  StateId = "StateId",
  ZipCode = "ZipCode",
  Phone = "Phone",
  Fax = "Fax",
  Email = "Email",
  Directions = "Directions",
  SponsorTypeId = "SponsorTypeId",
  SponsorType = "SponsorType",
}

export enum SkuRelationshipTypeIds {
  SuggestedCourses = "11fbf346-fd76-434c-b188-a1cf30202876",
  AutoRegister = "570b8899-255a-42e9-9109-f0877535902b",
}
